<template>
  <v-card>
    <v-progress-linear
      class="mt-2 mb-0"
      :color="getColor(task.status)"
      :height="task.status === 'in_progress' ? 20 : 22"
      :value="
        task.status === 'in_progress'
          ? task.processing_state && task.processing_state.value
            ? task.processing_state.value
            : 0
          : 100
      "
      striped
    >
      <template v-slot="{}">
        <strong v-if="task.status === 'todo'" class="white--text">
          Waiting for execution
        </strong>
        <strong
          v-if="task.status === 'in_progress' && task.processing_state"
          class="white--text"
        >
          {{ task.processing_state.label || 'Task in progress' }}
        </strong>
        <strong v-else-if="task.status === 'failed'" class="white--text">
          Failed
        </strong>
        <strong v-else-if="task.status === 'completed'" class="white--text">
          Completed
        </strong>
      </template>
    </v-progress-linear>
    <v-progress-linear
      v-if="task.status === 'in_progress'"
      class="mb-2"
      color="primary"
      height="2"
      indeterminate
    />
    <v-card-title>
      <v-menu offset-y v-if="role !== 'customer'">
        <template v-slot:activator="{ on }">
          <v-btn
            outlined
            color="success"
            v-on="on"
            :loading="
              reMatchLoading || sessionStatus.re_match_status === 'IN_PROGRESS'
            "
            :disabled="sessionStatus.re_match_status === 'IN_PROGRESS'"
          >
            <v-icon left>mdi-play-circle</v-icon>
            Re-match
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item
            :disabled="!sessionStatus.re_match_enabled"
            @click="initReMatch"
          >
            <v-list-item-content>
              <v-list-item-title>Regular re-match</v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                Make re-match of rows with appropriate status
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider />
          <v-list-item @click="initAdvancedReMatch">
            <v-list-item-content>
              <v-list-item-title>Advanced re-match</v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                Make re-match of all rows in version
                <br />
                <span class="red--text">
                  (ADVANCED FEATURE, please do not use as regular re-match)
                </span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        v-if="role !== 'customer'"
        class="ml-2"
        outlined
        color="primary"
        @click="initAnalyze"
        :loading="
          analyseLoading || sessionStatus.analyze_status === 'IN_PROGRESS'
        "
        :disabled="
          sessionStatus.analyze_status === 'IN_PROGRESS' ||
          sessionStatus.import_status === 'IN_PROGRESS'
        "
      >
        <v-icon left>mdi-cloud-search</v-icon>
        Analyze
      </v-btn>
      <v-btn
        v-if="role !== 'customer'"
        class="ml-2"
        outlined
        color="purple"
        @click="initImport"
        :loading="
          importLoading || sessionStatus.import_status === 'IN_PROGRESS'
        "
        :disabled="
          sessionStatus.analyze_status === 'IN_PROGRESS' ||
          sessionStatus.import_status === 'IN_PROGRESS'
        "
      >
        <v-icon left>mdi-cloud-upload</v-icon>
        Import
      </v-btn>
      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn
            class="ml-2"
            outlined
            color="accent"
            v-on="on"
            :disabled="bulkNumberOfRows.status === 0"
          >
            <v-icon color="black" left>mdi-dots-horizontal</v-icon>
            <span class="black--text">Change Status</span>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item
            v-for="status in rowStatusesComputed"
            :key="status.id"
            @click="initStatusChange(status.name)"
          >
            <v-list-item-icon>
              <v-icon :color="status.color">{{ status.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ status.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        v-if="role !== 'customer' && hasSourceDataLocation"
        class="ml-2"
        outlined
        color="primary"
        @click="checkSessionFiles"
        :loading="fetchingSessions"
      >
        <v-icon left>mdi-wrench-outline</v-icon>
        Preprocess and Analyse
        <template v-slot:loader>
          <v-progress-circular
            indeterminate
            class="mr-1"
            color="primary"
            size="18"
            width="2"
          />
          <span>{{ startPreprocessorStatusText }}</span>
        </template>
      </v-btn>
      <v-btn
        outlined
        class="ml-2"
        color="primary"
        target="_blank"
        :href="businessLink"
        v-if="role !== 'customer' && enterpriseSessionId && businessLink"
      >
        <v-icon>mdi-link-variant</v-icon>
      </v-btn>
      <v-spacer />
      <FileRowSearch @onSearchChange="handleSearchChange" />
      <FileFilters ref="fileFilters" @onChange="handleFiltersChange" />

      <v-menu>
        <template v-slot:activator="{ on: menu, attrs }">
          <v-tooltip bottom color="white">
            <template v-slot:activator="{ on: tooltip }">
              <v-btn
                v-bind="attrs"
                v-on="{ ...tooltip, ...menu }"
                icon
                color="primary"
              >
                <v-icon>mdi-file-tree</v-icon>
              </v-btn>
            </template>
            <span class="black--text">Click to pick a version</span>
          </v-tooltip>
        </template>
        <v-list>
          <v-list-item
            v-for="version in versions"
            :key="version.id"
            @click="selectVersion(version)"
          >
            <v-list-item-icon>
              <v-icon
                v-if="
                  String(selectedVersion) !== String(latestVersion.id) &&
                  String(latestVersion.id) === String(version.id)
                "
                color="purple"
              >
                mdi-alpha-l-circle-outline
              </v-icon>
              <v-icon
                v-if="String(selectedVersion) === String(version.id)"
                color="success"
              >
                mdi-check
              </v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              {{ formatDate(version.created_at) }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-menu>
        <template v-slot:activator="{ on: menu, attrs }">
          <v-tooltip bottom color="white">
            <template v-slot:activator="{ on: tooltip }">
              <v-btn
                v-bind="attrs"
                v-on="{ ...tooltip, ...menu }"
                icon
                color="primary"
              >
                <v-icon>mdi-share-variant</v-icon>
              </v-btn>
            </template>
            <span class="black--text">
              Click to generate the appropriate link.
            </span>
          </v-tooltip>
        </template>
        <v-list>
          <v-list-item
            @click="() => handleGenerateLink(LinksTypesMap.CUSTOMER_LINK)"
          >
            <v-list-item-title>Customer link</v-list-item-title>
          </v-list-item>
          <v-list-item
            @click="() => handleGenerateLink(LinksTypesMap.MIG_PAL_LINK)"
          >
            <v-list-item-title>MIG Pal link</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <FileReportsMenu :result="result" />

      <v-tooltip bottom color="white">
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            :disabled="
              result.migrationPollSteps && !result.migrationPollSteps.length
            "
            v-on="on"
            color="success"
            @click="openMigrationPollInfo"
          >
            <v-icon>mdi-account-voice</v-icon>
          </v-btn>
        </template>
        <span class="black--text">
          Click to check Migration PAL completed steps.
        </span>
      </v-tooltip>
    </v-card-title>
    <v-row dense>
      <v-col xs="6" sm="4" md="4">
        <FileStatistics
          :stats="statistics"
          :matchScoreStatistics="matchScoreStatistics"
          :revenue="totalRevenue"
        />
      </v-col>
      <v-col xs="6" sm="8" md="8">
        <FileStatusesFilters
          :breed-state="task.breedState"
          :errors="errors"
          :rowStatuses="rowStatuses"
          :role="role"
          @onChange="handleFiltersChange"
        />
      </v-col>
    </v-row>
    <AnalyzeDialog
      @onEnvChanged="onEnvChanged"
      @onAgree="onBulkAnalyze"
      :numberOfRows="bulkNumberOfRows.analyse"
      ref="analyzeDialog"
    />
    <ImportDialog
      @onEnvChanged="onEnvChanged"
      @onAgree="onBulkImport"
      :numberOfRows="bulkNumberOfRows.import"
      ref="importDialog"
    />
    <StatusChangeDialog @onAgree="onBulkSetStatus" ref="statusChangeDialog" />
    <ReMatchDialogV2
      v-if="$store.getters.isMatcherApiV2Enabled"
      @onAgree="onReMatch"
      ref="reMatchDialog"
    />
    <ReMatchDialog v-else @onAgree="onReMatch" ref="reMatchDialog" />
    <MappedCoreFileNotFoundDialog
      ref="mappedCoreFileNotFoundDialog"
      @onContinue="startPreprocessor"
    />
    <SmartStatusDialog
      ref="smartStatusDialog"
      @onChange="handleSmartStatusChange"
    />
    <MigrationPollInfoDialog ref="migrationPollInfoDialog" />
  </v-card>
</template>

<script>
import moment from 'moment';
import * as _ from 'lodash';
import FileStatistics from './FileStatistics';
import FileStatusesFilters from './FileStatusesFilters';
import AnalyzeDialog from './actionsDialogs/AnalyzeDialog';
import ImportDialog from './actionsDialogs/ImportDialog';
import StatusChangeDialog from './actionsDialogs/StatusChangeDialog';
import SmartStatusDialog from './actionsDialogs/SmartStatusDialog';
import FileFilters from '@/components/files/FileFilters';
import { DEFAULTS } from '@/constants';
import ReMatchDialogV2 from './actionsDialogs/ReMatchDialogV2.vue';
import ReMatchDialog from './actionsDialogs/ReMatchDialog.vue';
import MappedCoreFileNotFoundDialog from '@/components/files/actionsDialogs/MappedCoreFileNotFoundDialog';
import { GlobalConfig } from '@/shared/GlobalAppconfig.class';
import FileReportsMenu from '@/components/files/FileReportsMenu';
import {
  PreprocessorStartStatusesList,
  PortalPracticesLinksMap
} from '@/constants';
import { checkIsLastRunOld } from '@/helpers';
import MigrationPollInfoDialog from '@/components/dialogs/MigrationPollInfoDialog';
import FileRowSearch from '@/components/files/FileRowSearch';
import { DEFAULT_ROW_SEARCH_CONTEXT } from '@/constants';
import { businessApiV1, processingResultsApiV1, tasksApiV1 } from '@/api/v1';
import { businessApiV2, processingResultsApiV2, tasksApiV2 } from '@/api/v2';

const SESSION_CHECKING_MAPPED_CORE = 'checking mapped_core...';
const SESSION_CHECKING_TEXT = 'checking session...';
const PREPROCESSOR_STARTING_TEXT = 'Starting preprocessor...';
const SMART_READY_FOR_IMPORT_STATUS = 'smart_ready_for_import';
const SmartReadyForImportStatusOption = {
  title: 'Smart Ready for import',
  color: '#5eb319',
  icon: 'mdi-check-circle-outline',
  name: SMART_READY_FOR_IMPORT_STATUS
};

export default {
  props: {
    bulkNumberOfRows: {
      type: Object,
      default: () => ({
        status: 0,
        analyse: 0,
        import: 0
      })
    },
    multimatch: {
      type: Boolean,
      default: false
    },
    role: {
      type: String,
      default: ''
    },
    latestVersion: {
      type: Object,
      default: () => ({})
    },
    versions: {
      type: Array,
      default: () => []
    },
    errors: {
      type: Array,
      default: () => []
    },
    rowStatuses: {
      type: Array,
      default: () => []
    },
    task: {
      type: Object,
      default: () => ({})
    },
    statistics: {
      type: Object,
      default: () => ({})
    },
    sessionStatus: {
      type: Object,
      default: () => ({})
    },
    reMatchLoading: {
      type: Boolean,
      default: true
    },
    analyseLoading: {
      type: Boolean,
      default: true
    },
    importLoading: {
      type: Boolean,
      default: true
    },
    result: {
      type: Object,
      default: () => ({
        migrationPollSteps: []
      })
    }
  },
  computed: {
    hasSourceDataLocation() {
      return this.$store.getters.isMatcherApiV2Enabled
        ? Boolean(this.completedImportRequestId)
        : Boolean(this.enterpriseSessionId);
    },
    taskOptions() {
      return this.$store.getters.isMatcherApiV2Enabled
        ? {
            ...this.task.params,
            ...(this.task.params.context
              ? JSON.parse(this.task.params.context)
              : {})
          }
        : this.task.params;
    },
    rowStatusesComputed() {
      const isCustomer = this.role === 'customer';

      if (!this.rowStatuses?.length) return [];

      const statusesToMap = isCustomer
        ? this.showStatuses.customer
        : this.showStatuses.default;

      return statusesToMap.reduce(
        (statuses, name) => {
          const status = _.find(this.rowStatuses, { name });

          return [
            ...statuses,
            {
              ...status,
              icon: this.getStatusByName(name).icon,
              color: this.getStatusByName(name).color
            }
          ];
        },
        isCustomer ? [] : [SmartReadyForImportStatusOption]
      );
    }
  },
  data: () => ({
    showStatuses: DEFAULTS.SHOW_STATUSES,
    selectedFilters: [],
    searchEnabled: false,
    selectedVersion: 0,
    filters: {
      search: '',
      searchContext: DEFAULT_ROW_SEARCH_CONTEXT,
      statuses: [],
      multimatch: false,
      excludeIgnore: false,
      showPrice: true,
      reviewed: false,
      customerVerified: false
    },
    enterpriseSessionId: null,
    completedImportRequestId: null,
    fetchingSessions: false,
    startPreprocessorStatusText: SESSION_CHECKING_MAPPED_CORE,
    matchScoreStatistics: {},
    businessLink: null,
    LinksTypesMap: DEFAULTS.LINKS_TYPES_MAP,
    totalRevenue: null
  }),
  created() {
    GlobalConfig.config.notLatestVersion = false;
    this.selectedVersion = this.$route.params.result_id;
    this.fetchMatchScoresStatistics();
    this.fetchTotalRevenue();
  },
  beforeDestroy() {
    GlobalConfig.config.oldLastRun = false;
  },
  watch: {
    filters: {
      deep: true,
      handler(newValue) {
        clearTimeout(this.searchTimeout);

        this.searchTimeout = setTimeout(() => {
          this.$emit('onFiltersChanged', newValue);
        }, 300);
      }
    },
    selectedVersion() {
      if (!this.latestVersion.id) return;

      GlobalConfig.config.notLatestVersion =
        String(this.selectedVersion) !== String(this.latestVersion.id);
    },
    latestVersion() {
      if (!this.latestVersion.id) return;

      GlobalConfig.config.notLatestVersion =
        String(this.selectedVersion) !== String(this.latestVersion.id);
    },
    result(value) {
      const { env: businessEnv, rhapsody_id: businessRhapsodyId } = _.get(
        value,
        ['task', 'business'],
        {}
      );
      this.enterpriseSessionId = _.get(value, [
        'task',
        'rhapsody_enterprise_session_id'
      ]);
      this.completedImportRequestId = _.get(value, [
        'task',
        'rhapsody_completed_import_request_id'
      ]);
      this.businessLink =
        PortalPracticesLinksMap[businessEnv] + businessRhapsodyId;

      GlobalConfig.config.oldLastRun = checkIsLastRunOld(this.result);
    }
  },
  components: {
    FileStatistics,
    FileStatusesFilters,
    AnalyzeDialog,
    ImportDialog,
    StatusChangeDialog,
    ReMatchDialog,
    ReMatchDialogV2,
    FileReportsMenu,
    MappedCoreFileNotFoundDialog,
    SmartStatusDialog,
    FileFilters,
    MigrationPollInfoDialog,
    FileRowSearch
  },
  methods: {
    handleFiltersChange({ filter, value }) {
      this.filters[filter] = value;
    },
    getColor(status) {
      switch (status) {
        case 'completed':
          return 'success';
        case 'failed':
          return 'error';
        case 'in_progress':
          return 'primary';
        case 'todo':
          return 'accent';
      }
    },
    formatDate(dateString) {
      return moment(dateString).format('MM.DD-HH:mm');
    },
    selectVersion(version) {
      this.selectedVersion = version.id;
      this.$router.push({
        name: 'EditFile',
        params: {
          result_id: this.selectedVersion
        }
      });
    },
    resetMultimatch() {
      this.$refs.fileFilters.resetMultimatch();
    },
    getStatusByName(statusName) {
      const status = DEFAULTS.ROWS_STATUSES[statusName];
      if (status) return status;
      else {
        return DEFAULTS.ROWS_STATUSES.not_reviewed;
      }
    },
    handleGenerateLink(linkType) {
      this.$emit('onGenerateLink', linkType);
    },
    initReMatch() {
      this.$refs.reMatchDialog.open(this.taskOptions);
    },
    initAdvancedReMatch() {
      this.$refs.reMatchDialog.open(this.taskOptions, true);
    },
    initAnalyze() {
      this.$refs.analyzeDialog.open(this.task.id);
    },
    initImport() {
      this.$refs.importDialog.open(this.task.id);
    },
    initStatusChange(status) {
      if (status === SMART_READY_FOR_IMPORT_STATUS) {
        this.$refs.smartStatusDialog.open();
      } else {
        this.$refs.statusChangeDialog.open(
          status,
          this.bulkNumberOfRows.status
        );
      }
    },
    onEnvChanged(env) {
      this.$emit('onEnvChanged', env);
    },
    onReMatch(context, isAdvanced) {
      this.$emit('onReMatch', context, isAdvanced);
    },
    onBulkAnalyze() {
      this.$emit('onBulkAnalyze');
    },
    onBulkImport() {
      this.$emit('onBulkImport');
    },
    onBulkSetStatus(status) {
      this.$emit('onBulkSetStatus', status);
    },
    handleSmartStatusChange() {
      this.$emit('onSmartStatusApply');
    },
    async getSessionStatus() {
      try {
        this.fetchingSessions = true;
        this.startPreprocessorStatusText = SESSION_CHECKING_TEXT;
        const {
          task: {
            business: { rhapsody_id, env }
          }
        } = this.result;
        const sessions = await (
          this.$store.getters.isMatcherApiV2Enabled
            ? businessApiV2
            : businessApiV1
        ).fetchBusinessSessions(rhapsody_id, env);

        const sessionStatus = _.get(
          _.find(sessions, { id: this.enterpriseSessionId }),
          'status'
        );

        if (!sessionStatus) {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Session not found'
          });
          this.fetchingSessions = false;
          return;
        }

        return sessionStatus;
      } catch (err) {
        this.fetchingSessions = false;
        return err;
      }
    },
    async checkSessionFiles() {
      const sessionFiles = await this.fetchSessionFiles();
      const isMappedCoreExists = _.get(
        _.find(sessionFiles, { name: 'mapped_core' }),
        'exists'
      );

      if (isMappedCoreExists) {
        await this.startPreprocessor();
      } else {
        this.$refs.mappedCoreFileNotFoundDialog.open();
      }
    },
    async startPreprocessor() {
      try {
        // The Matcher V2 Sessions don't have status and do not pertain to this check
        if (!this.$store.getters.isMatcherApiV2Enabled) {
          const sessionStatus = await this.getSessionStatus();

          if (!PreprocessorStartStatusesList.includes(sessionStatus)) {
            this.$notify({
              type: 'warn',
              title: 'Warning',
              text: 'Preprocessor is busy now'
            });
            this.fetchingSessions = false;
            return;
          }
        }

        const taskId = _.get(this.result, ['task', 'id']);
        this.startPreprocessorStatusText = PREPROCESSOR_STARTING_TEXT;
        await (
          this.$store.getters.isMatcherApiV2Enabled ? tasksApiV2 : tasksApiV1
        ).startPreprocessor(taskId);

        this.$notify({
          type: 'success',
          title: 'Preprocessor started',
          text: 'Preprocessor and analyse successfully started'
        });

        this.fetchingSessions = false;
        this.startPreprocessorStatusText = SESSION_CHECKING_MAPPED_CORE;
      } catch (err) {
        this.fetchingSessions = false;
        console.log(err);
      }
    },
    fetchSessionFiles() {
      const {
        task: {
          business: { env }
        }
      } = this.result;
      this.fetchingSessions = true;

      return (
        this.$store.getters.isMatcherApiV2Enabled
          ? businessApiV2
          : businessApiV1
      )
        .fetchBusinessSessionFiles(this.enterpriseSessionId, env)
        .finally(() => (this.fetchingSessions = false));
    },
    async fetchMatchScoresStatistics() {
      const resultId = this.$route.params.result_id;

      if (resultId) {
        this.matchScoreStatistics = await (
          this.$store.getters.isMatcherApiV2Enabled
            ? processingResultsApiV2
            : processingResultsApiV1
        ).fetchMatchScoreStatistics(resultId);
      }
    },
    async fetchTotalRevenue() {
      const resultId = this.$route.params.result_id;

      if (resultId) {
        this.totalRevenue = await (
          this.$store.getters.isMatcherApiV2Enabled
            ? processingResultsApiV2
            : processingResultsApiV1
        ).fetchTotalRevenue(resultId);
      }
    },
    openMigrationPollInfo() {
      const migrationPollSteps = _.get(this.result, 'migrationPollSteps');
      this.$refs.migrationPollInfoDialog.open(migrationPollSteps);
    },
    handleSearchChange(searchContext, searchTerm) {
      this.filters.searchContext = searchContext;
      this.filters.search = searchTerm;
    }
  }
};
</script>
